// Helper compartido de emisión client-side de los eventos del embudo de
// onboarding (PRD-134-1, TC-134-1.4.5.1).
//
// Las TCs de instrumentación de la épica no vuelven a construir el contexto del
// evento: llaman a `emiteEventoDelEmbudo('nombre', { ...params propios })` y
// este helper le adjunta las propiedades comunes que el servidor ha publicado en
// `window.rankiaAnalitica` al renderizar la página. Así el evento que emite el
// navegador y el que emite el servidor (`Analitica::Embudo::EventoDelEmbudo`)
// llevan exactamente las mismas propiedades, que es la condición para poder
// encadenar los pasos del embudo en BigQuery.
//
// Mecanismo: `window.dataLayer.push` (GTM → GA4), el mismo que ya usan
// `multistep-wizard` y la analítica del modal de invitación
// (`analitica_modal_de_invitacion.js`). No se introduce ninguna librería nueva.
//
// Consentimiento: no se comprueba aquí. Lo decide GTM, cuyas etiquetas del
// embudo solo disparan si Didomi da el consentimiento de Google Analytics.
// Empujar al `dataLayer` sin consentimiento no envía nada a GA4. Lo que sí se
// hace aquí es esperar a que Didomi lo haya publicado (ver
// `cuandoGtmConozcaElConsentimiento`, en `consentimiento_en_gtm.js`).
import { cuandoGtmConozcaElConsentimiento } from 'lib/consentimiento_en_gtm'

// Clave global bajo la que el servidor publica el contexto. Estable: forma parte
// del contrato entre `AnaliticaDelEmbudo` (servidor) y este helper.
export const CLAVE_CONTEXTO = 'rankiaAnalitica'

// Espejo de `Analitica::Embudo::EventoDelEmbudo::CLAVES_PROHIBIDAS`. La regla
// "sin PII en payload" tiene que valer igual en los dos caminos de emisión: de
// nada sirve blindar el servidor si el navegador puede empujar un email.
//
// La paridad entre las dos listas la verifica
// `spec/services/analitica/embudo/evento_del_embudo_spec.rb`, que lee ESTE
// fichero: si se toca una lista y no la otra, el spec falla.
export const CLAVES_PROHIBIDAS = [
  'email', 'correo', 'password', 'contrasena', 'contraseña', 'token', 'secret',
  'nombre', 'apellido', 'surname', 'fullname', 'firstname', 'lastname', 'username',
  'user_name', 'first_name', 'last_name', 'real_name', 'display_name',
  'telefono', 'phone', 'movil', 'iban', 'tarjeta', 'direccion', 'address'
]

// Claves demasiado genéricas para compararlas por coincidencia parcial: se
// exigen completas. `field_name` o `step_name` son etiquetas del embudo, no
// nombres de persona, y son justo lo que hay que emitir al medir errores de
// campo. Espejo de `CLAVES_PROHIBIDAS_EXACTAS` del servidor.
export const CLAVES_PROHIBIDAS_EXACTAS = ['name', 'nie', 'dni', 'nif', 'pass']

// Claves que contienen una raíz prohibida pero no son un dato personal (una
// categoría del contrato), exigidas completas: `email_type` es el tipo de email
// de lifecycle (TC-134-1.4.8.7). Espejo de `CLAVES_PERMITIDAS` del servidor.
export const CLAVES_PERMITIDAS = ['email_type']

// Propiedades comunes publicadas por el servidor, o `null` si la
// instrumentación está apagada o la página no las ha publicado. Devuelve una
// copia: quien la use no puede contaminar el contexto de los eventos siguientes.
export function propiedadesComunesDelEmbudo () {
  const contexto = window[CLAVE_CONTEXTO]

  if (!contexto || typeof contexto !== 'object') return null
  if (contexto.embudoActivo === false) return null

  return { ...(contexto.propiedadesComunes || {}) }
}

// Emite un evento del embudo. Devuelve `true` si se ha empujado o queda en cola
// hasta que Didomi publique el consentimiento.
//
// Nunca lanza: la analítica no puede romper el flujo que mide (misma política
// que el guard sobre `dataLayer` de la analítica del modal de invitación, donde
// un GTM no cargado —dev sin consent, bloqueador, fallo de red— se llevaba por
// delante funcionalidad real).
export function emiteEventoDelEmbudo (nombre, params = {}) {
  if (!nombre) return false

  const comunes = propiedadesComunesDelEmbudo()
  if (comunes === null) return false
  if (!Array.isArray(window.dataLayer)) return false

  // Las propiedades comunes se aplican DESPUÉS de las propias del evento, igual
  // que server-side: son el contrato del embudo y ningún evento puede pisarlas.
  const propios = sinPii(params)
  delete propios.event

  const evento = {
    event: nombre,
    ...propios,
    ...comunes,
    // El `timestamp` del contexto es el del render de la página; el del evento
    // es el del momento en que ocurre. En una página de larga vida (el
    // formulario de registro) pueden separarse minutos, y el embudo mide justo
    // esa distancia. Se toma aquí y no al empujar, que puede ser más tarde.
    timestamp: new Date().toISOString()
  }

  cuandoGtmConozcaElConsentimiento(() => window.dataLayer.push(sinClavesHeredadas(evento)),
    window[CLAVE_CONTEXTO].esperaAlConsentimiento)

  return true
}

// Las claves que este helper ha empujado ya al `dataLayer` en esta página.
const clavesEmpujadas = new Set()

// GTM no lee el objeto empujado sino su modelo de datos, que fusiona cada `push` con los
// anteriores: una clave que el evento no trae conserva el valor que dejó el `push` previo, y la
// etiqueta de GA4 del wizard, que lee todos los parámetros de la familia en cada evento, se la
// mandaba. En producción `welcome_step_question_skipped` salía con el `skip_method` o la
// `duration_ms` del evento de paso que lo precede, y `welcome_categories_selected` con el
// `step_number` del completado (TDT-RNK-6698). Por eso cada `push` deja sin valor las claves que un
// evento anterior empujó y este no trae; GTM no manda a GA4 un parámetro `undefined`.
//
// Se calcula al empujar y no al emitir: los eventos que esperan a Didomi salen después, y lo que
// hay en el modelo es lo que dejó el último `push`, no el último evento emitido.
function sinClavesHeredadas (evento) {
  const heredadas = [...clavesEmpujadas].filter((clave) => !(clave in evento))
  Object.keys(evento).forEach((clave) => clavesEmpujadas.add(clave))

  return { ...Object.fromEntries(heredadas.map((clave) => [clave, undefined])), ...evento }
}

// Descarta las claves que puedan contener PII, por coincidencia parcial y sin
// distinguir mayúsculas (`email`, `user_email` y `emailHash` caen igual).
//
// Avisa por consola de lo que descarta: si una TC de instrumentación nombra mal
// un parámetro, el evento sale sin él y el hueco no se vería hasta mirar el dato
// en BigQuery semanas después.
export function sinPii (params) {
  const entradas = Object.entries(params || {})
  const descartadas = entradas.filter(([clave]) => esProhibida(clave)).map(([clave]) => clave)

  if (descartadas.length > 0) {
    console.warn(
      `[analitica-embudo] Parámetros descartados por la guarda de PII: ${descartadas.join(', ')}. ` +
      'Si no son datos personales, renómbralos.'
    )
  }

  return Object.fromEntries(entradas.filter(([clave]) => !esProhibida(clave)))
}

function esProhibida (clave) {
  const normalizada = String(clave).toLowerCase()

  if (CLAVES_PERMITIDAS.includes(normalizada)) return false
  if (CLAVES_PROHIBIDAS_EXACTAS.includes(normalizada)) return true

  return CLAVES_PROHIBIDAS.some((prohibida) => normalizada.includes(prohibida))
}
